import { WithAuth } from "@/hoc";
import React, { useEffect, useState } from "react";
import { useNavigate, useParams } from "react-router-dom";
import { Button, Flex, Grid, Icon, useMediaQuery } from "@chakra-ui/react";
import { ChevronLeftIcon, ChevronRightIcon } from "@chakra-ui/icons";
import { useCustomLink, usePlan } from "@/hooks";
import { Plan, CustomLink } from "@/models";
import { handleApiError } from "@/utils";
import { PunctualCharge } from "../seja-pago/punctual-charge";
import { CustomSignature } from "../seja-pago/custom-signature";
import { usePreviewContext } from "@/contexts/PreviewContext";
import PreviewLinkPagamento from "@/components/PreviewLinkPagamento";
import { BackButton } from "@/components";
import { Empty, PageHeading } from "barte-design-system";

const CustomLinksEdit = () => {
  const [plans, setPlans] = useState<Plan[]>([]);
  const [initialData, setInitialData] = useState({} as CustomLink);
  const [customizableDate, setCustomizableDate] = useState(false);
  const [isPreviewOpen, setIsPreviewOpen] = useState(false);
  const [isLoading, setIsLoading] = useState(true);
  const [hasError, setHasError] = useState(false);
  const isEditableLinkType = initialData?.type === "ORDER" || initialData?.type === "SUBSCRIPTION";
  const { previewData, setPreviewData } = usePreviewContext();
  const { id } = useParams();

  const { getCustomLinkData } = useCustomLink();
  const { getPlans } = usePlan();

  const [isLargeScreen] = useMediaQuery('(min-width: 980px)');
  const [showButtonPreview] = useMediaQuery('(min-width: 1240px)');
  const showPreview = isPreviewOpen;
  const navigate = useNavigate();

  useEffect(() => {
    if (isLargeScreen) {
      setIsPreviewOpen(true);
    } else {
      setIsPreviewOpen(false);
    }
  }, [isLargeScreen]);

  async function getInitialData() {
    setIsLoading(true);
    setHasError(false);

    try {
      const getPlansReponse = await getPlans();
      const response = await getCustomLinkData(String(id));
      setPlans(getPlansReponse?.data);
      setInitialData(response?.data);
      setCustomizableDate(response.data.scheduledDate ? true : false);
    } catch (error) {
      setHasError(true);

      const errors = error?.response?.data?.errors;

      console.error("[links-customizados] falha ao carregar o link de pagamento", {
        id,
        status: error?.response?.status,
        codes: Array.isArray(errors) ? errors.map((item) => item?.code) : undefined,
        message: error?.message,
      });

      handleApiError(error, "Não foi possível carregar esse link de pagamento.");
    } finally {
      setIsLoading(false);
    }
  }

  useEffect(() => {
    if (id === undefined) {
      setIsLoading(false);
      return;
    }
    getInitialData();
  }, [id]);

  return (
    <Flex
      flexDir="column"
      align="start"
      pb="20px"
    >
      <Flex w="100%" align="center" justify="flex-start" marginBottom="1rem">
      <PageHeading
        onBack={() => navigate("/seja-pago")}
        title="Editar link de pagamento"
      />
      </Flex>

      {isEditableLinkType && (
        <Grid
          templateColumns={showPreview ? "1fr 323px" : "1fr"}
          gap={showPreview ? "45px" : "45px"}
          h="80vh"
          w="100%"
          position="relative"
        >
          <Flex
            pr={showPreview ? "20px" : "0"}
            flexDir="column"
            overflowY="scroll"
            position="sticky"
            top="0"
            alignItems="center"
            css={{
              '&::-webkit-scrollbar': {
                display: 'none'
              },
              '-ms-overflow-style': 'none',
              'scrollbar-width': 'none'
            }}
            marginRight={showPreview ? "45px" : "45px"}
          >
            <Flex w="100%" maxW={{ base: "100%", xl: "820px" }} mx="auto">
              {initialData?.type === "ORDER" && (
                <PunctualCharge
                  asEdition
                  initialData={initialData}
                  previewData={previewData}
                  setPreviewData={setPreviewData}
                />
              )}
              {initialData?.type === "SUBSCRIPTION" && (
                <CustomSignature
                  asEdition
                  initialData={initialData}
                  initialDataPlan={plans}
                  setPreviewData={setPreviewData}
                  previewData={previewData}
                />
              )}
            </Flex>
          </Flex>

          {!showButtonPreview && (
            <Button
              position="fixed"
              right={showPreview ? "415px" : "20px"}
              top="50%"
              transform="translateY(-50%)"
              zIndex="20"
              size="sm"
              variant="outline"
              bg="white"
              borderColor="gray.300"
              onClick={() => setIsPreviewOpen(!isPreviewOpen)}
              transition="all 0.3s ease"
              minW="40px"
              h="40px"
              borderRadius="50%"
              _hover={{
                bg: "gray.50",
                borderColor: "gray.400"
              }}
            >
              <Icon
                as={showPreview ? ChevronRightIcon : ChevronLeftIcon}
                w={4}
                h={4}
              />
            </Button>
          )}

          {showPreview && (
            <Flex
              position="fixed"
              right="107px"
              top="70px"
              w="323px"
              flexDir="column"
              bg="white"
              h="100vh"
              zIndex="10"
              borderLeft={isLargeScreen ? "none" : "1px solid #E0E0E0"}
            >
              <PreviewLinkPagamento
                previewData={previewData}
                isPontual={initialData?.type === "ORDER"}
              />
            </Flex>
          )}
        </Grid>
      )}

      {!isLoading && hasError && (
        <Flex w="100%" justifyContent="center" paddingY="3rem">
          <Empty
            type="error"
            description={"Não foi possível carregar esse link de pagamento.\nTente novamente em instantes."}
            onRetry={getInitialData}
          />
        </Flex>
      )}

      {!isLoading && !hasError && !isEditableLinkType && (
        <Flex w="100%" justifyContent="center" paddingY="3rem">
          <Empty
            type="empty"
            description={"Esse link de pagamento não pode ser editado.\nEle pode ter sido removido ou ser de um tipo que não é mais suportado."}
            buttonLabel="Voltar para links de pagamento"
            onRetry={() => navigate("/seja-pago")}
          />
        </Flex>
      )}
    </Flex>
  );
};

export default WithAuth(CustomLinksEdit);
